import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { showToast, Text } from '@diamond/shared/ui';
import { formatDateRange } from '@diamond/shared/utils';
import { FormProvider, UseFormReturn } from 'react-hook-form';

import { useUploadVoucherBannerMutation } from '../hooks';
import type { CreateVoucherRequest } from '../types';
import { ImageDropzone } from './image-dropzone';

interface VoucherEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  form: UseFormReturn<CreateVoucherRequest>;
  onSubmit: () => Promise<void>;
  isSubmitting?: boolean;
  quotaUsed?: number;
}

const parseNullableNumber = (value: string) => {
  const parsed = Number(value);
  return isNaN(parsed) ? null : parsed;
};

export function VoucherEditModal({
  isOpen,
  onClose,
  form,
  onSubmit,
  isSubmitting = false,
  quotaUsed = 0,
}: VoucherEditModalProps) {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
    getValues,
  } = form;
  const toast = useToast();

  const periodStartDate = watch('period_start_date');
  const periodEndDate = watch('period_end_date');
  const titleValue = watch('title') ?? '';
  const descriptionValue = watch('desc') ?? '';
  const quotaValue = watch('target_configuration.quota') ?? 0;

  const uploadBannerMutation = useUploadVoucherBannerMutation({
    onSuccess: (result) => {
      const previousUrl = getValues('banner_url');
      if (previousUrl && previousUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previousUrl);
      }
      setValue('banner_url', result.url, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      clearErrors('banner_url');
      showToast(toast, 'success', 'Banner berhasil diunggah');
    },
    onError: (error) => {
      showToast(
        toast,
        'error',
        error.message || 'Gagal mengunggah banner voucher'
      );
    },
  });

  const handleBannerUpload = (files: File[]) => {
    if (!files?.length) return;
    uploadBannerMutation.mutate(files[0]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Voucher</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormProvider {...form}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={6} align="stretch">
                {/* Banner Upload */}
                <Box>
                  <ImageDropzone
                    name="banner_url"
                    accept={{
                      'image/png': ['.png'],
                      'image/jpeg': ['.jpg', '.jpeg'],
                    }}
                    maxSize={2}
                    errors={errors}
                    placeholder="Drag and drop file here"
                    onFileUpload={handleBannerUpload}
                    isUploading={uploadBannerMutation.isPending}
                  />
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Hanya menerima format PNG, JPG dengan ukuran maksimal 2 MB
                    <br />
                    Banner Resolution: 1312 x 512 px
                  </Text>
                </Box>

                {/* Judul Voucher */}
                <FormControl isInvalid={!!errors.title}>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="semibold"
                    color="gray.900"
                  >
                    Judul Voucher
                  </FormLabel>
                  <Input
                    {...register('title')}
                    placeholder="Masukkan Judul Banner"
                    maxLength={100}
                    fontSize="sm"
                    autoComplete="off"
                  />
                  <Text fontSize="xs" color="gray.500" textAlign="right" mt={1}>
                    {titleValue.length}/100
                  </Text>
                  <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
                </FormControl>

                {/* Masa Berlaku */}
                <FormControl
                  isInvalid={
                    !!errors.period_start_date || !!errors.period_end_date
                  }
                >
                  <FormLabel
                    fontSize="sm"
                    fontWeight="semibold"
                    color="gray.900"
                  >
                    Masa Berlaku
                  </FormLabel>
                  <Flex gap={3} align="center">
                    <Input
                      {...register('period_start_date')}
                      type="date"
                      placeholder="dd/mm/yyyy"
                      fontSize="sm"
                    />
                    <Text color="gray.400" fontSize="sm">
                      -
                    </Text>
                    <Input
                      {...register('period_end_date')}
                      type="date"
                      placeholder="dd/mm/yyyy"
                      fontSize="sm"
                    />
                  </Flex>
                  {periodStartDate && periodEndDate && (
                    <Text fontSize="xs" color="gray.600" mt={2}>
                      {formatDateRange(periodStartDate, periodEndDate)}
                    </Text>
                  )}
                  <FormErrorMessage>
                    {errors.period_start_date?.message ||
                      errors.period_end_date?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* Deskripsi */}
                <FormControl isInvalid={!!errors.desc}>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="semibold"
                    color="gray.900"
                  >
                    Deskripsi
                  </FormLabel>
                  <Textarea
                    {...register('desc')}
                    placeholder="Masukkan deskripsi voucher"
                    rows={4}
                    maxLength={200}
                    fontSize="sm"
                    resize="vertical"
                  />
                  <Text fontSize="xs" color="gray.500" textAlign="right" mt={1}>
                    {descriptionValue.length}/200
                  </Text>
                  <FormErrorMessage>{errors.desc?.message}</FormErrorMessage>
                </FormControl>

                {/* Ketersediaan Voucher & Maks. Pakai */}
                <Flex gap={4}>
                  <FormControl
                    isInvalid={!!errors.target_configuration?.quota}
                    flex={1}
                  >
                    <FormLabel
                      fontSize="sm"
                      fontWeight="semibold"
                      color="gray.900"
                    >
                      Ketersediaan Voucher
                    </FormLabel>
                    <Input
                      type="number"
                      min={1}
                      placeholder="Masukkan kuantitas"
                      fontSize="sm"
                      {...register('target_configuration.quota', {
                        setValueAs: parseNullableNumber,
                      })}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {quotaUsed} / {quotaValue} telah beredar
                    </Text>
                    <FormErrorMessage>
                      {errors.target_configuration?.quota?.message as string}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors.target_configuration?.max_use_per_user}
                    flex={1}
                  >
                    <FormLabel
                      fontSize="sm"
                      fontWeight="semibold"
                      color="gray.900"
                    >
                      Maks. pakai per shipto.
                    </FormLabel>
                    <Input
                      type="number"
                      min={1}
                      placeholder="Masukkan batas"
                      fontSize="sm"
                      {...register('target_configuration.max_use_per_user', {
                        setValueAs: parseNullableNumber,
                      })}
                    />
                    <FormErrorMessage>
                      {
                        errors.target_configuration?.max_use_per_user
                          ?.message as string
                      }
                    </FormErrorMessage>
                  </FormControl>
                </Flex>

                {/* Action Buttons */}
                <Flex justify="flex-end" gap={3} pt={2}>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    isDisabled={isSubmitting}
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    bg="#0000FF"
                    color="white"
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                    _hover={{ bg: '#0000DD' }}
                    _active={{ bg: '#0000BB' }}
                  >
                    Simpan
                  </Button>
                </Flex>
              </VStack>
            </form>
          </FormProvider>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
